import React from 'react';
import { Bell, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { Notification } from '../types';
import {
  useNotificationMutations,
  useRecentNotifications,
  useUnreadNotificationCount,
} from '../hooks/useNotifications';
import { openNotificationLink } from '../utils/openNotificationLink';

const formatCount = (count: number) => (count > 10 ? '10+' : String(count));
const formatBadgeCount = (count: number) => (count > 9 ? '9+' : String(count));

const formatDate = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));

interface NotificationRowProps {
  notification: Notification;
  onOpen: (notification: Notification) => void;
}

const NotificationRow: React.FC<NotificationRowProps> = ({ notification, onOpen }) => (
  <button
    type="button"
    className={cn(
      'w-full border-b border-border px-3 py-3 text-left transition-colors last:border-b-0 hover:bg-accent',
      !notification.is_read && 'bg-accent/40',
    )}
    onClick={() => onOpen(notification)}
  >
    <div className="flex items-start gap-2">
      <span
        className={cn(
          'mt-1.5 h-2 w-2 flex-shrink-0 rounded-full',
          notification.is_read ? 'bg-transparent' : 'bg-primary',
        )}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <p className="truncate text-sm font-medium text-foreground">{notification.title}</p>
          <span className="flex-shrink-0 text-xs text-muted-foreground">
            {formatDate(notification.created_at)}
          </span>
        </div>
        <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{notification.message}</p>
      </div>
    </div>
  </button>
);

export const NotificationsBell: React.FC = () => {
  const navigate = useNavigate();
  const { data: recentNotifications, isLoading, isError } = useRecentNotifications();
  const { data: unreadCount } = useUnreadNotificationCount();
  const { markRead } = useNotificationMutations();

  const notifications = recentNotifications?.results ?? [];
  const totalCount = recentNotifications?.count ?? 0;
  const unread = unreadCount?.count ?? 0;

  const openNotification = async (notification: Notification) => {
    if (!notification.is_read) {
      await markRead.mutateAsync(notification.id);
    }
    openNotificationLink(notification.link_url, navigate);
  };

  const openAllNotifications = () => {
    navigate('/app/account?tab=notifications');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative mr-2 h-9 w-9 rounded-full"
          aria-label="Open notifications"
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span className="absolute -right-1 -top-1 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
              {formatBadgeCount(unread)}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 max-w-[calc(100vw-2rem)] p-0">
        <DropdownMenuLabel className="flex items-center justify-between px-4 py-3">
          <span>Notifications</span>
          {unread > 0 && <span className="text-xs text-muted-foreground">{unread} unread</span>}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="m-0" />

        <div className="max-h-96 overflow-y-auto">
          {isLoading && (
            <div className="flex items-center justify-center gap-2 px-4 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading notifications...
            </div>
          )}
          {isError && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              Notifications could not be loaded.
            </div>
          )}
          {!isLoading && !isError && notifications.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              No notifications yet.
            </div>
          )}
          {!isLoading &&
            !isError &&
            notifications.map((notification) => (
              <NotificationRow
                key={notification.id}
                notification={notification}
                onOpen={openNotification}
              />
            ))}
        </div>

        <DropdownMenuSeparator className="m-0" />
        <div className="p-2">
          <Button variant="ghost" className="w-full justify-center" onClick={openAllNotifications}>
            See All ({formatCount(totalCount)})
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
